import { FormikProps } from 'formik';
import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Application, AppNavigationState, S3Location, UserAuthContext } from 'src/components/context/AppContextModels';
import { useAppContext } from 'src/components/context/AppContextProvider';
import { useAuth } from 'src/components/context/AuthContextProvider';
import { ShowModalInfo } from 'src/components/generic-components/ConfirmationModal';
import { appSyncOperationName, eFinSuiteAdminMessages } from 'src/constants/AppConstants';
import * as mutations from 'src/graphql/mutations';
import { logger } from 'src/logger';
import { getCurrentUTCTimeZoneInISO } from 'src/utilities/DateTimeUtilities';
import { ApplicationFormValues } from '../../AdminModels';
import { fetchAllApplications } from 'src/api/app-sync-services';
import { FinTechTeams } from '../../teams-manage/TeamManagementModel';
import { FinSuiteAppMessages } from '../../AdminMessageConstants';
import { fetchGraphQLRequest } from 'src/api/graphql-api';
import { CreateApplicationMutation, UpdateApplicationMutation } from 'src/API';
import { AppLogoService } from 'src/services/AppLogoService';
import { ApplicationFormSchema, collectFailedYupTests, HTTPS_URL_TEST_NAME } from './ApplicationFormValidations';

export const INITIAL_FORM_VALUES: ApplicationFormValues = {
  id: null,
  applicationDetails: {
    applicationName: '',
    isFlagship: false,
    applicationLink: '',
    applicationOwnerLabelInput: {
      id: '',
      label: '',
      labelType: 'ApplicationOwner'
    },
    shortApplicationDescription: '',
    fullApplicationDescription: '',
    glAccounts: {
      label: '',
      id: '',
      labelType: 'GLAccount'
    },
    toolUI: {
      label: '',
      id: '',
      labelType: 'ToolUI'
    },
    embeddingEnabled: false,
    qChatConfig: null,
    mcpConfig: null,
    mcpPath: null,
    urlName: null,
    logoConfig: {
      logoType: 'bundled',
      selectedLogoName: null,
      uploadedFileName: null,
      s3Location: null
    }
  },
  linksAndTags: {
    hasNonProductionEnvironments: false,
    nonProductionEnvironments: [],
    timeGranularity: [],
    keyDimension: [],
    process: [],
    scenario: [],
    lastEnhanced: []
  },
  itemMetadata: {
    isActive: true,
    createdBy: '',
    createdTime: '',
    updatedBy: '',
    updatedTime: ''
  }
};

export const removeQuotesFromString = (str: string) => {
  try {
    if (typeof str === 'string') {
      if (str.startsWith('"') && str.endsWith('"')) {
        return str.slice(1, -1);
      }
      return str;
    }
  } catch (error) {
    console.error('Error occurred while removing quotes:', error);
  }

  return str;
};

export const useApplicationForm = () => {
  const navigate = useNavigate();
  const userAuthDetails = useAuth();
  const appContext = useAppContext();
  const formRef = useRef<FormikProps<ApplicationFormValues>>(null);

  const [stepError, setStepError] = useState('');
  const [action, setAction] = useState<'Create' | 'Update'>('Create');

  const [showModalInfo, setShowModalInfo] = useState<ShowModalInfo>({
    showModal: false,
    eventHeader: '',
    eventContentType: ''
  });

  const [fromUrl, setFromUrl] = React.useState('');

  const onCancel = () => {
    closeModal();
  };

  const closeModal = () => {
    setShowModalInfo({
      showModal: false,
      eventHeader: '',
      eventContentType: ''
    });
  };

  const onFormCancel = () => {
    setShowModalInfo({
      showModal: true,
      eventHeader: 'Cancel',
      eventContentType: 'CANCEL_APPLICATION',
      messageInfo: {
        applicationName: formRef.current?.values.applicationDetails.applicationName
      }
    });
  };

  const navigateBackToFrom = (message: string) => {
    const applicationName = formRef.current?.values.applicationDetails.applicationName || '';
    const navigationStateMessage: AppNavigationState =
      message === 'success'
        ? {
            message: {
              type: 'success',
              content: FinSuiteAppMessages.SUCCESSFULLY_UPDATES_NEW_APP(applicationName),
              header: ''
            }
          }
        : {};

    const navigatePath = fromUrl || '/admin/applications';
    navigate(navigatePath, { state: navigationStateMessage });
  };

  const onConfirm = async (showModalInfo: ShowModalInfo) => {
    showModalInfo.eventContentType === 'CANCEL_APPLICATION' && navigateBackToFrom('cancel');
  };

  const onSubmit = async () => {
    formRef.current
      ?.validateForm()
      .then(async (validationErrors) => {
        if (Object.keys(validationErrors).length === 0) {
          setStepError('');
          const application = formValuesToAPI(formRef.current!.values, userAuthDetails, action);

          // For Update: move logo to final path BEFORE the mutation so temp path never enters the DB.
          const logoConfig = formRef.current?.values.applicationDetails.logoConfig;
          let tempS3Location: S3Location | null = null;
          if (logoConfig?.logoType === 'app-specific' && logoConfig.s3Location?.key?.startsWith('tmp-logos/')) {
            tempS3Location = logoConfig.s3Location;
            if (action === 'Update' && application.id) {
              try {
                const finalLocation = await AppLogoService.copyToFinal(tempS3Location, application.id);
                application.logoConfig = { ...logoConfig, s3Location: finalLocation };
              } catch (copyError) {
                logger.error('Failed to copy logo to final path before update.', { error: copyError });
              }
            }
          }

          const mutation = action === 'Create' ? createApplication(application) : updateApplication(application);
          mutation
            .then(async (response) => {
              logger.info(`Successfully ${action}d Application.`, { info: response });

              // For Create: copy logo to final path using the new appId, then update the record
              if (action === 'Create' && tempS3Location) {
                const appId = response?.id;
                if (appId) {
                  try {
                    const finalLocation = await AppLogoService.copyToFinal(tempS3Location, appId);
                    application.id = appId;
                    application.logoConfig = { ...logoConfig!, s3Location: finalLocation };
                    await updateApplication(application);
                  } catch (moveError) {
                    logger.error('Failed to finalize logo for new app.', { error: moveError });
                  }
                }
              }

              // Best-effort cleanup of temp file
              if (tempS3Location) {
                AppLogoService.deleteLogo(tempS3Location).catch((e) => logger.warn('Orphaned temp logo', { key: tempS3Location!.key, error: e }));
              }

              // Save MCP config if enabled
              const mcpFormData = formRef.current?.values.applicationDetails?.mcpConfig;
              if (mcpFormData?.isEnabled) {
                const appId = response?.id || application.id;
                const mcpId = appId || '';
                if (!mcpId) {
                  logger.error('Cannot create MCP config without valid application ID.');
                } else {
                  try {
                    const mcpInput = {
                      id: mcpId,
                      name: application.applicationName || '',
                      description: mcpFormData.description || null,
                      category: mcpFormData.category || null,
                      logoConfig: application.logoConfig || null,
                      isFeatured: mcpFormData.isFeatured || false,

                      terminalCommands: (mcpFormData.terminalCommands || []).filter((tc) => tc.os.length > 0 && tc.command),
                      resources: (mcpFormData.resources || []).filter((r) => r.label && r.url),
                      toolGroups: mcpFormData.toolGroups || [],
                      features: (mcpFormData.features || []).filter((f) => f.title && f.description),
                      contacts: (mcpFormData.contacts || []).filter((c) => c.alias)
                    };

                    // Try update first, create if it doesn't exist
                    try {
                      await fetchGraphQLRequest<any>({
                        query: mutations.updateMcpConfig,
                        variables: { input: mcpInput },
                        requestName: 'updateMcpConfig',
                        requestType: appSyncOperationName.MUTATION
                      });
                    } catch (updateError: any) {
                      // Only fall through to create if not found; rethrow other errors
                      const isNotFound =
                        updateError?.message?.includes('not found') ||
                        updateError?.message?.includes('does not exist') ||
                        updateError?.message?.includes('ConditionalCheckFailed');
                      if (isNotFound) {
                        await fetchGraphQLRequest<any>({
                          query: mutations.createMcpConfig,
                          variables: { input: mcpInput },
                          requestName: 'createMcpConfig',
                          requestType: appSyncOperationName.MUTATION
                        });
                      } else {
                        throw updateError;
                      }
                    }

                    // Set mcpPath on the application
                    const mcpPath = `/mcp/${mcpId}`;
                    if (application.mcpPath !== mcpPath) {
                      await updateApplication({ ...application, id: response?.id || application.id, mcpPath });
                    }

                    logger.info('Successfully saved MCP config.', { mcpId });
                  } catch (mcpError) {
                    logger.error('Failed to save MCP config (app saved successfully).', { error: mcpError });
                  }
                }
              } else if (formRef.current?.initialValues?.applicationDetails?.mcpConfig?.isEnabled) {
                // Clean up MCP config if it was previously enabled
                const appId = response?.id || application.id;
                const mcpId = appId || '';
                if (mcpId) {
                  try {
                    await fetchGraphQLRequest<any>({
                      query: mutations.deleteMcpConfig,
                      variables: { id: mcpId },
                      requestName: 'deleteMcpConfig',
                      requestType: appSyncOperationName.MUTATION
                    });
                    logger.info('Successfully deleted MCP config.', { mcpId });
                    // Clear mcpPath on the application
                    if (application.mcpPath) {
                      await updateApplication({ ...application, id: response?.id || application.id, mcpPath: null });
                    }
                  } catch (deleteError: any) {
                    const isNotFound = deleteError?.message?.includes('not found') || deleteError?.message?.includes('does not exist');
                    if (!isNotFound) {
                      logger.error('Failed to delete MCP config.', { error: deleteError });
                    }
                  }
                }
              }

              const fetchAllApplicationsResponse = await fetchAllApplications(appContext?.teamPageContent);
              appContext.setListOfApplications(fetchAllApplicationsResponse.allApplications);
              appContext.setParsedApplications(fetchAllApplicationsResponse.applicationFlatTableEntity);

              const applicationFlashMessage =
                action === 'Create'
                  ? FinSuiteAppMessages.SUCCESSFULLY_ADDS_NEW_APP(application.applicationName!)
                  : FinSuiteAppMessages.SUCCESSFULLY_UPDATES_NEW_APP(application.applicationName!);
              appContext.displayApplicationFlashMessage(applicationFlashMessage, 'success');

              navigateBackToFrom('success');
            })
            .catch((error: any) => {
              // The backend resolver rejects a duplicate/reserved urlName with a
              // specific message; surface a clean, actionable version in the
              // flashbar instead of the generic save-failure text.
              const rawMessage: string = error?.message || '';
              let applicationFlashMessage: string =
                action === 'Create' ? FinSuiteAppMessages.UNSUCCESSFULLY_ADDS_NEW_APP : FinSuiteAppMessages.UNSUCCESSFULLY_UPDATES_NEW_APP;
              if (/urlname/i.test(rawMessage)) {
                applicationFlashMessage =
                  'This App-specific Path is already in use by another application. Please choose a different App-specific Path.';
              }
              setStepError(applicationFlashMessage);
              logger.error(`Unable to submit ${action} Application request.`, error);
            });
        } else {
          // SECURITY / UX: if any Yup failure is the URL-scheme check, show the
          // link-specific message ("Links must be valid https URLs.") instead of
          // the generic "Complete all required fields" message. Keeps the generic
          // message for pure required-field misses.
          //
          // NOTE: `validationErrors` from Formik is a tree of *messages only* —
          // the underlying Yup `.type` (test name) is dropped. To keep control
          // flow decoupled from display copy, we re-run the schema here and
          // inspect the failed test names. This means renaming/localizing
          // `eFinSuiteAdminMessages.VALID_URL` no longer silently breaks this
          // branch. The uniqueness async check is intentionally skipped
          // (`shouldSkipAppNameUniquenessValidation=true`, empty applicationNames)
          // because it isn't relevant to the URL-vs-required decision here.
          const failedTests = await collectFailedYupTests(ApplicationFormSchema([], true), formRef.current!.values);
          const hasUrlNameError = [...failedTests].some((testName) => testName.startsWith('url-name'));
          const hasUrlError = failedTests.has(HTTPS_URL_TEST_NAME);
          // Prioritize the URL-path-specific message: it's the field the admin is
          // actively editing, and the generic/link messages would mislead them.
          let stepErrorMessage = eFinSuiteAdminMessages.NEXT_BUTTON_VALIDATION_ERROR;
          if (hasUrlNameError) {
            stepErrorMessage = eFinSuiteAdminMessages.URL_PATH_VALIDATION_ERROR;
          } else if (hasUrlError) {
            stepErrorMessage = eFinSuiteAdminMessages.LINK_VALIDATION_ERROR;
          }
          setStepError(stepErrorMessage);
          setAllFieldsAsTouched();
        }
      })
      .catch((error: any) => {
        const errorMessage =
          action === 'Create' ? FinSuiteAppMessages.UNSUCCESSFULLY_ADDS_NEW_APP : FinSuiteAppMessages.UNSUCCESSFULLY_UPDATES_NEW_APP;
        setStepError(errorMessage);
        logger.error(`Unable to submit ${action} Application request.`, error);
      });
  };

  const setAllFieldsAsTouched = () => {
    formRef.current?.setTouched({
      applicationDetails: {
        applicationName: true,
        applicationLink: true,
        urlName: true,
        applicationOwnerLabelInput: {
          id: true,
          label: true,
          labelType: true
        },
        shortApplicationDescription: true,
        fullApplicationDescription: true,
        glAccounts: { id: true, label: true },
        toolUI: { id: true, label: true },
        qChatConfig: {
          isEnabled: true,
          agentUrl: true
        },
        logoConfig: {
          selectedLogoName: true,
          uploadedFileName: true,
          s3Location: true
        }
      } as any,
      linksAndTags: {
        hasNonProductionEnvironments: true,
        nonProductionEnvironments: true,
        keyDimension: [{ label: true }],
        process: [{ label: true }],
        scenario: [{ label: true }],
        lastEnhanced: [{ label: true }],
        timeGranularity: [{ label: true }]
      }
    });
  };

  return {
    stepError,
    setStepError,
    action,
    setAction,
    applicationFormRef: formRef,
    onFormCancel,
    onSubmit,
    showModalInfo,
    onCancel,
    onConfirm,
    fromUrl,
    setFromUrl
  };
};

const createApplication = async (inputForCreateApplication: Application) => {
  const createApplicationResponse = await fetchGraphQLRequest<CreateApplicationMutation>({
    query: mutations.createApplication,
    variables: { input: inputForCreateApplication },
    requestName: 'createApplication',
    requestType: appSyncOperationName.MUTATION
  });

  return createApplicationResponse.createApplication;
};

const updateApplication = async (inputForCreateApplication: Application) => {
  const updateApplicationResponse = await fetchGraphQLRequest<UpdateApplicationMutation>({
    query: mutations.updateApplication,
    variables: { input: inputForCreateApplication },
    requestName: 'updateApplication',
    requestType: appSyncOperationName.MUTATION
  });
  return updateApplicationResponse.updateApplication;
};

export const formValuesToAPI = (formValues: ApplicationFormValues, userDetails: UserAuthContext, action: 'Create' | 'Update'): Application => {
  const formToAPIObject: Application = {
    id: formValues.id || '',
    applicationName: formValues.applicationDetails.applicationName,
    isFlagship: formValues.applicationDetails.isFlagship,

    applicationOwnerId: formValues.applicationDetails.applicationOwnerLabelInput.id,
    applicationLink: formValues.applicationDetails.applicationLink,
    shortApplicationDescription: formValues.applicationDetails.shortApplicationDescription,
    fullApplicationDescription: formValues.applicationDetails.fullApplicationDescription,
    glAccounts: [formValues.applicationDetails.glAccounts],
    toolUI: [formValues.applicationDetails.toolUI],
    embeddingEnabled: formValues.applicationDetails.embeddingEnabled,
    qChatConfig: formValues.applicationDetails.qChatConfig || null,
    mcpPath: formValues.applicationDetails.mcpPath || null,
    urlName: formValues.applicationDetails.urlName || null,
    logoConfig: formValues.applicationDetails.logoConfig || null,
    hasNonProductionEnvironments: formValues.linksAndTags.hasNonProductionEnvironments,
    nonProductionEnvironments: formValues.linksAndTags.nonProductionEnvironments?.map((nonProdEnv) => {
      return {
        ...nonProdEnv,
        accessLevel: [nonProdEnv.accessLevel]
      };
    }),
    timeGranularity: formValues.linksAndTags.timeGranularity,
    keyDimension: formValues.linksAndTags.keyDimension,
    process: formValues.linksAndTags.process,
    scenario: formValues.linksAndTags.scenario,
    lastEnhanced: formValues.linksAndTags.lastEnhanced,
    itemMetadata: {
      isActive: formValues.itemMetadata.isActive,
      createdBy: action === 'Create' ? userDetails.Alias : formValues.itemMetadata.createdBy,
      createdTime: action === 'Create' ? getCurrentUTCTimeZoneInISO() : formValues.itemMetadata.createdTime,
      updatedBy: userDetails.Alias,
      updatedTime: getCurrentUTCTimeZoneInISO()
    }
  };

  return formToAPIObject;
};

export const apiToFormValues = (application: Application, teamContent: FinTechTeams): ApplicationFormValues => {
  const teamName = teamContent?.teams?.find((team) => team.id === application.applicationOwnerId)?.teamName || '';

  return {
    id: application.id,
    applicationDetails: {
      applicationName: application.applicationName || '',
      isFlagship: application.isFlagship ?? false,
      applicationOwnerLabelInput: {
        id: application.applicationOwnerId,
        label: teamName,
        labelType: 'ApplicationOwner'
      },
      applicationLink: application.applicationLink || '',
      shortApplicationDescription: application.shortApplicationDescription || '',
      fullApplicationDescription: application.fullApplicationDescription || '',
      glAccounts: application.glAccounts[0],
      toolUI: application.toolUI[0],
      embeddingEnabled: application.embeddingEnabled || false,
      qChatConfig: application.qChatConfig || null,
      mcpPath: application.mcpPath || null,
      urlName: application.urlName || null,
      mcpConfig: application.mcpPath ? { isEnabled: true } : null,
      logoConfig: application.logoConfig?.logoType
        ? application.logoConfig
        : {
            logoType: 'bundled',
            selectedLogoName: null,
            uploadedFileName: null,
            s3Location: null
          }
    },
    linksAndTags: {
      hasNonProductionEnvironments: application.hasNonProductionEnvironments,
      nonProductionEnvironments: application.nonProductionEnvironments?.map((nonProdEnv) => {
        return {
          ...nonProdEnv,
          accessLevel: nonProdEnv.accessLevel[0]
        };
      }),
      timeGranularity: application.timeGranularity || [],
      keyDimension: application.keyDimension || [],
      process: application.process || [],
      scenario: application.scenario || [],
      lastEnhanced: application.lastEnhanced || []
    },
    itemMetadata: {
      isActive: application.itemMetadata.isActive || false,
      createdBy: application.itemMetadata.createdBy || '',
      createdTime: application.itemMetadata.createdTime,
      updatedBy: application.itemMetadata.updatedBy || '',
      updatedTime: application.itemMetadata.updatedTime
    }
  };
};
